import { useEffect } from 'react';

interface Metric {
  name: string;
  value: number;
  rating?: 'good' | 'needs-improvement' | 'poor';
}

interface LayoutShiftEntry extends PerformanceEntry {
  hadRecentInput?: boolean;
  value: number;
}

/**
 * Hook to monitor Web Vitals (LCP, FID, CLS, TTFB)
 * Logs metrics to console and can be extended to send to analytics
 */
export const useWebVitals = () => {
  useEffect(() => {
    // Monitor LCP (Largest Contentful Paint)
    if ('PerformanceObserver' in window) {
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1] as PerformanceEntryList[0] & { renderTime?: number; loadTime?: number };
          const lcp: Metric = {
            name: 'LCP',
            value: (lastEntry.renderTime || lastEntry.loadTime) ?? 0,
            rating: ((lastEntry.renderTime || lastEntry.loadTime) ?? 0) < 2500 ? 'good' : 'poor',
          };
          if (process.env.NODE_ENV === 'development') {
            console.log('📊 Web Vitals - LCP:', lcp.value.toFixed(0), 'ms', `(${lcp.rating})`);
          }
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

        // Monitor CLS (Cumulative Layout Shift)
        const clsObserver = new PerformanceObserver((list) => {
          let clsValue = 0;
          for (const entry of list.getEntries()) {
            const layoutEntry = entry as LayoutShiftEntry;
            if (!layoutEntry.hadRecentInput) {
              clsValue += layoutEntry.value;
            }
          }
          const cls: Metric = {
            name: 'CLS',
            value: clsValue,
            rating: clsValue < 0.1 ? 'good' : 'poor',
          };
          if (process.env.NODE_ENV === 'development') {
            console.log('📊 Web Vitals - CLS:', cls.value.toFixed(3), `(${cls.rating})`);
          }
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });

        // Monitor FCP (First Contentful Paint)
        const fcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            const fcp: Metric = {
              name: 'FCP',
              value: entry.startTime,
              rating: entry.startTime < 1800 ? 'good' : 'poor',
            };
            if (process.env.NODE_ENV === 'development') {
              console.log('📊 Web Vitals - FCP:', fcp.value.toFixed(0), 'ms', `(${fcp.rating})`);
            }
          });
        });
        fcpObserver.observe({ entryTypes: ['paint'] });
      } catch (e) {
        // Silently fail if PerformanceObserver is not available
        console.debug('PerformanceObserver not available');
      }
    }

    // Log navigation timing info
    if ('PerformanceTiming' in window) {
      window.addEventListener('load', () => {
        const perfData = window.performance.timing;
        const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
        const domReadyTime = perfData.domContentLoadedEventEnd - perfData.navigationStart;

        if (process.env.NODE_ENV === 'development') {
          console.log('📊 Performance Summary:');
          console.log('  Page Load Time:', pageLoadTime, 'ms');
          console.log('  DOM Ready Time:', domReadyTime, 'ms');
          console.log('  DNS Lookup:', perfData.domainLookupEnd - perfData.domainLookupStart, 'ms');
          console.log('  TCP Connection:', perfData.connectEnd - perfData.connectStart, 'ms');
          console.log('  Request Time:', perfData.responseStart - perfData.requestStart, 'ms');
        }
      });
    }
  }, []);
};
